import {containsNonStandardAminoAcids} from 'biojs/Util';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  MAX_AMINO_ACID_COUNT,
  MAX_CHAI1_TOKEN_COUNT,
  MAX_MULTIMER_COMPONENTS,
} from 'molbio/proteins/util/StructurePredictionConstants';

export interface ComponentData {
  aminoAcids: string;
  count: number;
  numHeavyAtoms: number;
  entityType: ModelTypeConstants;
}

export interface SelectedComponents {
  [componentId: string]: ComponentData;
}

export function calculateTotalAminoAcidCount(components: SelectedComponents): number {
  const {proteinComponents} = separateComponentsByEntityType(components);
  return Object.values(proteinComponents).reduce(
    (total, component) => total + component.aminoAcids.length * component.count,
    0
  );
}

export function calculateTotalHeavyAtomCount(components: SelectedComponents): number {
  const {moleculeComponents} = separateComponentsByEntityType(components);
  return Object.values(moleculeComponents).reduce(
    (total, component) => total + component.numHeavyAtoms * component.count,
    0
  );
}

export function calculateTotalTokenCount(components: SelectedComponents): number {
  const aminoAcidCount = calculateTotalAminoAcidCount(components);
  const heavyAtomCount = calculateTotalHeavyAtomCount(components);
  return aminoAcidCount + heavyAtomCount;
}

export const separateComponentsByEntityType = (
  components: SelectedComponents
): {
  moleculeComponents: SelectedComponents;
  proteinComponents: SelectedComponents;
} => ({
  moleculeComponents: Object.fromEntries(
    Object.entries(components).filter(([, component]) => component.entityType === ModelTypeConstants.MOLECULE)
  ),
  proteinComponents: Object.fromEntries(
    Object.entries(components).filter(([, component]) => component.entityType === ModelTypeConstants.PROTEIN)
  ),
});

export function isComponentSelectionValid(components: SelectedComponents): boolean {
  return getComponentValidationResult(components) == null;
}

export function flattenComponentSelectionToComponentIds(components: SelectedComponents): {
  proteinIds: Array<string>;
  moleculeIds: Array<string>;
} {
  const proteinIds: Array<string> = [];
  const moleculeIds: Array<string> = [];

  Object.entries(components).forEach(([componentId, component]) => {
    const targetArray = component.entityType === ModelTypeConstants.PROTEIN ? proteinIds : moleculeIds;
    targetArray.push(...Array(component.count).fill(componentId));
  });
  return {proteinIds, moleculeIds};
}

export type ValidationError =
  | {
      type: 'no_components';
    }
  | {
      type: 'empty_amino_acids';
      componentIds: Array<string>;
    }
  | {
      type: 'non_standard_amino_acids';
      componentIds: Array<string>;
    }
  | {
      type: 'count_exceeded';
      totalCount: number;
    }
  | {
      type: 'too_many_components';
      componentCount: number;
    }
  | {
      type: 'too_many_tokens';
      tokenCount: number;
    };

export type ComponentValidationResult = Array<ValidationError>;

export function getComponentCount(components: SelectedComponents): number {
  return Object.values(components).reduce((acc, component) => acc + component.count, 0);
}

export function getComponentValidationResult(
  components: SelectedComponents
): ComponentValidationResult | null {
  const errors: ComponentValidationResult = [];
  const {moleculeComponents, proteinComponents} = separateComponentsByEntityType(components);
  const moleculeComponentValidationResult = getMoleculeComponentValidationResult(moleculeComponents);
  const proteinComponentValidationResult = getProteinComponentValidationResult(proteinComponents);
  if (moleculeComponentValidationResult != null) {
    errors.push(...moleculeComponentValidationResult);
  }
  if (proteinComponentValidationResult != null) {
    errors.push(...proteinComponentValidationResult);
  }

  const totalTokenCount = calculateTotalTokenCount(components);
  if (totalTokenCount > MAX_CHAI1_TOKEN_COUNT) {
    errors.push({
      type: 'too_many_tokens',
      tokenCount: totalTokenCount,
    });
  }

  return errors.length > 0 ? errors : null;
}

export function getMoleculeComponentValidationResult(
  components: SelectedComponents
): ComponentValidationResult | null {
  const errors: ComponentValidationResult = [];

  return errors.length > 0 ? errors : null;
}

export function getProteinComponentValidationResult(
  components: SelectedComponents
): ComponentValidationResult | null {
  const errors: ComponentValidationResult = [];

  if (Object.keys(components).length === 0) {
    errors.push({type: 'no_components'});
    return errors;
  }

  // Check for empty amino acids in any component
  const componentsWithEmptyAminoAcids: Array<string> = [];
  for (const [componentId, component] of Object.entries(components)) {
    if (component.aminoAcids.length === 0) {
      componentsWithEmptyAminoAcids.push(componentId);
    }
  }

  if (componentsWithEmptyAminoAcids.length > 0) {
    errors.push({
      type: 'empty_amino_acids',
      componentIds: componentsWithEmptyAminoAcids,
    });
  }

  // Check for non-standard amino acids in any component
  const componentsWithNonStandardAminoAcids: Array<string> = [];
  for (const [componentId, component] of Object.entries(components)) {
    if (containsNonStandardAminoAcids(component.aminoAcids)) {
      componentsWithNonStandardAminoAcids.push(componentId);
    }
  }

  if (componentsWithNonStandardAminoAcids.length > 0) {
    errors.push({
      type: 'non_standard_amino_acids',
      componentIds: componentsWithNonStandardAminoAcids,
    });
  }

  const totalCount = calculateTotalAminoAcidCount(components);
  if (totalCount > MAX_AMINO_ACID_COUNT) {
    errors.push({
      type: 'count_exceeded',
      totalCount,
    });
  }

  const componentCount = getComponentCount(components);
  if (componentCount > MAX_MULTIMER_COMPONENTS) {
    errors.push({
      type: 'too_many_components',
      componentCount,
    });
  }

  return errors.length > 0 ? errors : null;
}

export function addComponent(
  components: SelectedComponents,
  componentId: string,
  componentData: Omit<ComponentData, 'count'>
): SelectedComponents {
  const updatedComponents = {...components};

  if (updatedComponents[componentId]) {
    // Increment count of existing component
    updatedComponents[componentId] = {
      ...updatedComponents[componentId],
      count: updatedComponents[componentId].count + 1,
    };
  } else {
    // Add new component with count 1
    updatedComponents[componentId] = {...componentData, count: 1};
  }

  return updatedComponents;
}

export function addComponents(
  components: SelectedComponents,
  newComponents: Array<{
    id: string;
    aminoAcids: string;
    entityType: ModelTypeConstants;
    numHeavyAtoms: number;
  }>
): SelectedComponents {
  let updatedComponents = components;

  newComponents.forEach((newComponent) => {
    updatedComponents = addComponent(updatedComponents, newComponent.id, {
      aminoAcids: newComponent.aminoAcids,
      entityType: newComponent.entityType,
      numHeavyAtoms: newComponent.numHeavyAtoms,
    });
  });

  return updatedComponents;
}

export function incrementComponent(components: SelectedComponents, componentId: string): SelectedComponents {
  if (!components[componentId]) {
    return components;
  }

  return {
    ...components,
    [componentId]: {
      ...components[componentId],
      count: components[componentId].count + 1,
    },
  };
}

export function decrementComponent(components: SelectedComponents, componentId: string): SelectedComponents {
  if (!components[componentId]) {
    return components;
  }

  const component = components[componentId];
  const updatedComponents = {...components};

  if (component.count <= 1) {
    // Remove component if count reaches 0
    delete updatedComponents[componentId];
  } else {
    updatedComponents[componentId] = {
      ...component,
      count: component.count - 1,
    };
  }

  return updatedComponents;
}

export function removeComponent(components: SelectedComponents, componentId: string): SelectedComponents {
  const updatedComponents = {...components};
  delete updatedComponents[componentId];
  return updatedComponents;
}
